import React, { useRef, useState } from "react";
import { ArrowLeft, Palette, Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import PlatformProfileMenu from "@/components/PlatformProfileMenu";
import "./BrandStudioActionShapesV1.css";
import { listMaterialProjects } from "@/lib/brandStudioApi";

const SPACES = [
  { id: "my-brand", label: "Мой бренд", path: "/brand-studio/my-brand" },
  { id: "create-material", label: "Создать материал", path: "/brand-studio/possibilities" },
  { id: "workshop", label: "Моя мастерская", path: "/brand-studio/workshop" },
];

// Only existing navigable tools. Unbuilt catalogue formats are not search results.
const AVAILABLE_TOOLS = [
  { label: "Создание бренда", path: "/brand-studio/create" },
  { label: "Логотип", path: "/brand-studio/create?step=logo&intent=edit" },
  { label: "Цветовая система", path: "/brand-studio/create?step=visual-style&intent=edit" },
  { label: "Типографика", path: "/brand-studio/create?step=typography&intent=edit" },
  { label: "Референсы", path: "/brand-studio/references" },
  { label: "Визуальное направление", path: "/brand-studio/visual-direction" },
  { label: "Благодарственная карточка", path: "/brand-studio/thank-you" },
  { label: "Бирка", path: "/brand-studio/tag" },
];

export default function BrandStudioShellV2({ activeSpace, children }) {
  const nav = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [projectMatches, setProjectMatches] = useState([]);
  const [pageLimitReached, setPageLimitReached] = useState(false);
  const ticket = useRef(0);
  const results = SPACES.filter((item) => item.label.toLocaleLowerCase("ru").includes(searched.toLocaleLowerCase("ru")));
  const toolMatches = AVAILABLE_TOOLS.filter((item) => item.label.toLocaleLowerCase("ru").includes(searched.toLocaleLowerCase("ru")));
  const clearSearch = () => {
    ticket.current += 1;
    setQuery("");
    setSearched("");
    setProjectMatches([]);
    setSearchError("");
    setLoadingSearch(false);
    setPageLimitReached(false);
  };
  const submitSearch = async (event) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) { clearSearch(); return; }
    const current = ++ticket.current;
    setSearched(value);
    setLoadingSearch(true);
    setSearchError("");
    setProjectMatches([]);
    try {
      // The same owner-scoped, region-local source as «Моя мастерская».
      const projects = await listMaterialProjects();
      if (ticket.current !== current) return;
      const term = value.toLocaleLowerCase("ru");
      const typeLabel = (type) => type === "tag" ? "Бирка" : type === "premium_order_tag" ? "Бирка концепция" : "Благодарственная карточка";
      const matches = projects.filter((project) =>
        String(project.project_title || "").toLocaleLowerCase("ru").includes(term) ||
        typeLabel(project.material_type).toLocaleLowerCase("ru").includes(term));
      setProjectMatches(matches.map((project) => ({
        key: project.project_id,
        title: project.project_title || "Без названия",
        path: "/brand-studio/" +
          (project.material_type === "tag" ? "tag" : project.material_type === "premium_order_tag" ? "premium-order-tag" : "thank-you") +
          "?project=" + encodeURIComponent(project.project_id),
      })));
      setPageLimitReached(projects.length >= 100);
    } catch (error) {
      if (ticket.current === current) setSearchError("Не удалось загрузить свои материалы. Повторите поиск.");
    } finally {
      if (ticket.current === current) setLoadingSearch(false);
    }
  };

  const isActive = (item) => {
    if (activeSpace) return activeSpace === item.id;
    return location.pathname === item.path;
  };

  return (
    <div className="min-h-[calc(100dvh-116px)] sm:min-h-[calc(100dvh-56px)] bg-[#F6F1E9]" data-testid="brand-studio-shell-v2">
      <header className="border-b border-[#E5D9C8] bg-[#FFFDF9]">
        <div className="mx-auto flex h-[54px] w-full max-w-[1360px] items-center gap-3 px-4 md:px-8">
          <button
            type="button"
            onClick={() => nav("/hall")}
            className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-xl px-2.5 text-[12px] text-[#5B4637] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50"
            style={{ boxShadow: "inset 0 0 0 1px #E6D9C7" }}
            data-testid="brand-studio-v2-back-to-hall"
          >
            <ArrowLeft size={14} />
            <span className="hidden sm:inline">Anokina Studio</span>
          </button>

          <button
            type="button"
            onClick={() => nav("/brand-studio")}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            data-testid="brand-studio-v2-home-link"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#F0E5D5] text-[#755D46]" aria-hidden="true">
              <Palette size={14} />
            </span>
            <span
              className="truncate text-[17px] leading-none text-[#2B211A]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Brand Studio
            </span>
          </button>

          <form role="search" onSubmit={submitSearch} className="hidden min-w-0 items-center gap-2 sm:flex" data-testid="brand-studio-global-search">
            <Search size={15} className="shrink-0 text-[#755D46]" aria-hidden="true" />
            <input value={query} onChange={(event) => { ticket.current += 1; setQuery(event.target.value); setSearched(""); setProjectMatches([]); setSearchError(""); setLoadingSearch(false); }}
              aria-label="Поиск по разделам Brand Studio" placeholder="Найти пространство"
              className="w-36 min-w-0 rounded-lg border border-[#E5D9C8] bg-white px-2 py-2 text-xs outline-none md:w-48" />
            <button type="submit" className="min-h-10 rounded-lg bg-[#4A3728] px-3 text-xs text-white">Найти</button>
          </form>
          <PlatformProfileMenu
            user={user}
            onLogout={async () => {
              await logout();
              nav("/");
            }}
          />
        </div>

        <form role="search" onSubmit={submitSearch} className="flex gap-2 px-4 py-2 sm:hidden" data-testid="brand-studio-mobile-search">
          <input value={query} onChange={(event) => { ticket.current += 1; setQuery(event.target.value); setSearched(""); setProjectMatches([]); setSearchError(""); setLoadingSearch(false); }}
            aria-label="Поиск по разделам Brand Studio" placeholder="Найти пространство"
            className="min-w-0 flex-1 rounded-lg border border-[#E5D9C8] px-3 text-sm" />
          <button type="submit" className="min-h-10 rounded-lg bg-[#4A3728] px-4 text-sm text-white">Найти</button>
        </form>
        {searched && <div className="mx-auto w-full max-w-[1360px] px-4 py-3 md:px-8" data-testid="brand-studio-search-results" aria-live="polite">
          {loadingSearch && <p className="text-sm text-[#755D46]">Ищем Ваши материалы…</p>}
          {searchError && <p role="alert" className="text-sm text-[#8A3F1E]">{searchError}</p>}
          {(results.length > 0 || toolMatches.length > 0 || projectMatches.length > 0) && <div className="flex flex-wrap gap-2">
            {results.map((item) => <button key={item.id} type="button" onClick={() => { clearSearch(); nav(item.path); }}
              className="min-h-10 rounded-xl border border-[#DCCAB4] bg-[#F7F0E6] px-3 text-sm text-[#3E2E20]">Раздел: {item.label}</button>)}
            {toolMatches.map((item) => <button key={item.path} type="button" onClick={() => { clearSearch(); nav(item.path); }}
              className="min-h-10 rounded-xl border border-[#DCCAB4] bg-[#F7F0E6] px-3 text-sm text-[#3E2E20]">Инструмент: {item.label}</button>)}
            {projectMatches.map((item) => <button key={item.key} type="button" onClick={() => { clearSearch(); nav(item.path); }}
              className="min-h-10 rounded-xl border border-[#DCCAB4] bg-white px-3 text-sm text-[#3E2E20]">Материал: {item.title}</button>)}
          </div>}
          {!loadingSearch && !searchError && !results.length && !toolMatches.length && !projectMatches.length &&
            <p className="text-sm text-[#755D46]">По запросу «{searched}» ничего не найдено.</p>}
          {pageLimitReached && <p className="mt-2 text-xs text-[#755D46]">Поиск охватывает последние 100 материалов мастерской. Более ранние материалы пока не включены.</p>}
        </div>}
        <nav
          className="border-t border-[#EEE4D6] bg-[#FFFDF9]"
          aria-label="Разделы Brand Studio"
          data-testid="brand-studio-v2-primary-nav"
        >
          <div className="mx-auto grid w-full max-w-[1360px] grid-cols-3 gap-0 px-3 sm:flex sm:gap-1 md:px-8">
            {SPACES.map((item) => {
              const active = isActive(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => nav(item.path)}
                  className={
                    "relative flex min-h-10 w-full items-center justify-center px-2 text-[12.5px] transition sm:min-h-11 sm:w-auto sm:shrink-0 sm:justify-start sm:px-3 sm:text-[13px] " +
                    (active
                      ? "font-medium text-[#2E2118]"
                      : "text-[#796652] hover:text-[#3E2E20]")
                  }
                  data-testid={`brand-studio-nav-${item.id}`}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                  {active && (
                    <span
                      className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#7D6248] sm:inset-x-3"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="mx-auto w-full max-w-[1360px] min-w-0 overflow-x-clip px-4 py-4 md:px-8 md:py-5">
        {children}
      </main>
    </div>
  );
}
